import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const PaymentReturn = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'failed'>('loading');
  const [customerEmail, setCustomerEmail] = useState<string>('');

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      checkSessionStatus();
    } else {
      setStatus('failed');
    }
  }, [sessionId]);

  const checkSessionStatus = async () => {
    try {
      // For now, just assume success if we have a session ID
      // The webhook will handle updating the subscription in the background
      if (sessionId) {
        setStatus('success');
        setCustomerEmail(''); // We don't have access to this from client-side
        
        // Check if we're in the middle of onboarding
        const isOnboarding = sessionStorage.getItem('onboarding-in-progress') === 'true';
        
        // Redirect after a short delay
        setTimeout(() => {
          if (isOnboarding) {
            // Return to onboarding
            navigate('/?continueOnboarding=true');
          } else {
            navigate('/?welcome=true');
          }
        }, 3000);
      } else {
        setStatus('failed');
      }
    } catch (error) {
      console.error('Error checking session status:', error);
      setStatus('failed');
    }
  };

  const goToDashboard = () => {
    navigate('/');
  };

  const goToPricing = () => {
    // Check if we were in onboarding
    const isOnboarding = sessionStorage.getItem('onboarding-in-progress') === 'true';
    
    if (isOnboarding) {
      // Return to onboarding at plan selection step
      navigate('/?continueOnboarding=true&paymentFailed=true');
    } else {
      navigate('/pricing');
    }
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-gray-50 flex items-center justify-center">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
              Processing Payment
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-muted-foreground">
              Please wait while we confirm your payment...
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-gray-50 flex items-center justify-center">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="text-center">
              <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-4" />
              Payment Successful!
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <div>
              <p className="text-lg font-medium mb-2">Welcome to Step Premium!</p>
              <p className="text-muted-foreground">
                Your subscription is now active. You now have access to all premium features.
              </p>
              {customerEmail && (
                <p className="text-sm text-muted-foreground mt-2">
                  Confirmation sent to {customerEmail}
                </p>
              )}
            </div>
            
            <div className="pt-2">
              <p className="text-sm text-muted-foreground">
                You'll be redirected automatically in a few seconds
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50 flex items-center justify-center">
      <Card className="max-w-md w-full">
        <CardHeader>
          <CardTitle className="text-center">
            <XCircle className="h-12 w-12 text-red-600 mx-auto mb-4" />
            Payment Failed
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          <div>
            <p className="text-lg font-medium mb-2">Something went wrong</p>
            <p className="text-muted-foreground">
              Your payment could not be processed. Please try again or contact support if the issue persists.
            </p>
          </div>
          
          <div className="space-y-2">
            <Button onClick={goToPricing} className="w-full">
              Try Again
            </Button>
            <Button variant="outline" onClick={goToDashboard} className="w-full">
              Back to Dashboard
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentReturn;